<template>
  <div class="workspace">
    <header class="topbar">
      <div class="brand">
        <img :src="logo" alt="" />
        <div>
          <strong>经销商工作台</strong>
          <span>HEY STAR BUSINESS</span>
        </div>
      </div>

      <div class="topbar-actions">
        <button class="icon-button" type="button" title="刷新" aria-label="刷新" @click="refresh">↻</button>
        <button class="icon-button" type="button" title="全屏" aria-label="切换全屏" @click="toggleFullscreen">⛶</button>
        <span class="account">{{ userInfo.account || '···' }}</span>
        <button class="button" type="button" @click="showPasswordModal = true">修改密码</button>
        <button class="button button-danger" type="button" @click="showLogoutModal = true">退出</button>
      </div>
    </header>

    <main class="workspace-main">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" :key="viewKey" />
        </transition>
      </router-view>
    </main>

    <transition name="fade">
      <div v-if="showLogoutModal" class="modal-mask" role="presentation" @click.self="showLogoutModal = false">
        <section class="modal" role="dialog" aria-modal="true" aria-labelledby="logout-title">
          <header class="modal-header">
            <h2 id="logout-title">退出登录</h2>
            <button class="icon-button" type="button" aria-label="关闭" @click="showLogoutModal = false">×</button>
          </header>
          <div class="modal-body">确定退出当前账号？</div>
          <footer class="modal-footer">
            <button class="button" type="button" @click="showLogoutModal = false">取消</button>
            <button class="button button-danger" type="button" @click="logout">退出</button>
          </footer>
        </section>
      </div>
    </transition>

    <transition name="fade">
      <div v-if="showPasswordModal" class="modal-mask" role="presentation" @click.self="closePasswordModal">
        <form class="modal" role="dialog" aria-modal="true" aria-labelledby="password-title" @submit.prevent="submitPassword">
          <input class="visually-hidden" type="text" name="username" :value="userInfo.account" autocomplete="username" tabindex="-1" readonly />
          <header class="modal-header">
            <h2 id="password-title">修改密码</h2>
            <button class="icon-button" type="button" aria-label="关闭" @click="closePasswordModal">×</button>
          </header>
          <div class="modal-body password-fields">
            <div class="field">
              <label for="old-password">原密码</label>
              <input id="old-password" v-model.trim="passwordForm.oldPassword" class="input" type="password" autocomplete="current-password" />
            </div>
            <div class="field">
              <label for="new-password">新密码</label>
              <input id="new-password" v-model.trim="passwordForm.newPassword" class="input" type="password" autocomplete="new-password" />
            </div>
            <div class="field">
              <label for="confirm-password">确认新密码</label>
              <input id="confirm-password" v-model.trim="passwordForm.againNewPwd" class="input" type="password" autocomplete="new-password" />
            </div>
            <p class="password-error" role="alert">{{ passwordError }}</p>
          </div>
          <footer class="modal-footer">
            <button class="button" type="button" @click="closePasswordModal">取消</button>
            <button class="button button-primary" type="submit" :disabled="passwordSubmitting">
              {{ passwordSubmitting ? '提交中...' : '确认修改' }}
            </button>
          </footer>
        </form>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import logo from '@/assets/image/logo.png'

const store = useStore()
const router = useRouter()
const userInfo = computed(() => store.getters.getUser)
const viewKey = ref(0)
const showLogoutModal = ref(false)
const showPasswordModal = ref(false)
const passwordSubmitting = ref(false)
const passwordError = ref('')
const passwordForm = reactive({ oldPassword: '', newPassword: '', againNewPwd: '' })

const refresh = () => viewKey.value++
const toggleFullscreen = async () => {
  if (document.fullscreenElement) await document.exitFullscreen()
  else await document.documentElement.requestFullscreen()
}
const logout = () => {
  store.commit('setLoginOut')
  showLogoutModal.value = false
  router.replace('/login')
}
const closePasswordModal = () => {
  showPasswordModal.value = false
  passwordError.value = ''
  Object.assign(passwordForm, { oldPassword: '', newPassword: '', againNewPwd: '' })
}
const submitPassword = async () => {
  const passwordPattern = /^[A-Za-z0-9]{6,12}$/
  if (!passwordPattern.test(passwordForm.oldPassword) || !passwordPattern.test(passwordForm.newPassword)) {
    passwordError.value = '密码须为 6 至 12 位数字或字母'
    return
  }
  if (passwordForm.newPassword !== passwordForm.againNewPwd) {
    passwordError.value = '两次输入的新密码不一致'
    return
  }

  passwordSubmitting.value = true
  passwordError.value = ''
  try {
    const result = await store.dispatch('fetchUpdateDealerUserPassword', {
      oldPassword: passwordForm.oldPassword,
      newPassword: passwordForm.newPassword
    })
    if (result?.code === 200) {
      store.commit('setError', { status: true, title: '密码已修改', message: '请重新登录', type: 'success' })
      closePasswordModal()
      logout()
    }
  } finally {
    passwordSubmitting.value = false
  }
}

const handleEscape = event => {
  if (event.key !== 'Escape') return
  showLogoutModal.value = false
  closePasswordModal()
}
onMounted(() => window.addEventListener('keydown', handleEscape))
onBeforeUnmount(() => window.removeEventListener('keydown', handleEscape))
</script>

<style scoped>
.workspace { height: 100%; background: #eef1ef; }
.topbar {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 22px;
  border-bottom: 1px solid var(--line);
  background: #fff;
}
.brand, .topbar-actions { display: flex; align-items: center; }
.brand { gap: 10px; }
.brand img { width: 34px; height: 34px; object-fit: contain; }
.brand div { display: grid; gap: 1px; }
.brand strong { font-size: 16px; }
.brand span { color: #7b847f; font-family: Consolas, monospace; font-size: 9px; letter-spacing: 1.4px; }
.topbar-actions { gap: 7px; }
.account { max-width: 150px; margin: 0 8px; overflow: hidden; font-size: 13px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.workspace-main { height: calc(100% - 58px); overflow: auto; }
.password-fields { display: grid; gap: 15px; }
.password-error { min-height: 18px; margin: 0; color: var(--danger); font-size: 12px; }
.visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
@media (max-width: 720px) {
  .topbar { align-items: flex-start; gap: 12px; padding: 10px 12px; }
  .topbar-actions { justify-content: flex-end; flex-wrap: wrap; }
  .account { display: none; }
  .workspace-main { height: calc(100% - 82px); }
}
</style>
